import { ColumnDef } from '@tanstack/react-table';
import { createContext, Fragment, useContext, useMemo } from 'react';
import { TableFilters, TableState } from './types';

interface TableContextValue<T = unknown, F extends TableFilters<T> = TableFilters<T>> {
    state: TableState<T, F>;
    onStateChange: (state: TableState<T, F>) => void;
    columns: ColumnDef<T, any>[]; // eslint-disable-line @typescript-eslint/no-explicit-any
}

const TableContext = createContext<TableContextValue | null>(null);

export const TableProvider = <T, F extends TableFilters<T> = TableFilters<T>>({
    state,
    onStateChange,
    columns,
    children,
    as: As = Fragment,
}: TableContextValue<T, F> & {
    children: React.ReactNode;
    as?: React.ElementType;
}) => {
    const value = useMemo(
        () => ({
            columns,
            onStateChange,
            state,
        }),
        [columns, onStateChange, state]
    );
    return (
        <TableContext.Provider value={value as unknown as TableContextValue<unknown, TableFilters<unknown>>}>
            <As>{children}</As>
        </TableContext.Provider>
    );
};

export const useTableContext = <T, F extends TableFilters<T> = TableFilters<T>>() => {
    const context = useContext(TableContext);
    if (!context) {
        throw Error('TableContext not initialized');
    }
    return context as unknown as TableContextValue<T, F>;
};
